// ユーザー管理サービスの実装
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { User } from '../types';
import { auth } from '../firebase/config';
import { updateProfile } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export class UserService {
  private static readonly COLLECTION_NAME = 'users';

  // ユーザー一覧取得
  static async getUsers(): Promise<User[]> {
    try {
      // 一旦orderByを削除して、全ユーザーを取得
      const q = query(collection(db, this.COLLECTION_NAME));
      
      const querySnapshot = await getDocs(q);
      
      const users: User[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        users.push({
          id: doc.id,
          email: data.email || '',
          displayName: data.displayName || undefined,
          photoURL: data.photoURL || undefined,
          role: data.role || 'viewer',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        });
      });
      
      // createdAtでソート（メモリ上で）
      users.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      return users;
    } catch (error: any) {
      console.error('ユーザー一覧の取得に失敗しました:', error);
      console.error('エラー詳細:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // ユーザー詳細取得
  static async getUser(userId: string): Promise<User | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const data = docSnap.data();
      return {
        id: docSnap.id,
        email: data.email || '',
        displayName: data.displayName || undefined,
        photoURL: data.photoURL || undefined,
        role: data.role || 'viewer',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    } catch (error) {
      console.error('ユーザー情報の取得に失敗しました:', error);
      throw error;
    }
  }

  // ユーザー登録（管理者ログイン時のみ。サーバーAPIで作成するためログインユーザーは保持される）
  static async createUser(
    email: string,
    password: string,
    displayName?: string,
    role?: 'admin' | 'viewer'
  ): Promise<User> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('ログインしていません。管理者でログインしてください。');
    }

    const idToken = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/admin/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        email,
        password,
        displayName: displayName || undefined,
        role: role || 'viewer',
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'ユーザー登録に失敗しました');
    }
    return {
      id: data.id,
      email: data.email,
      displayName: data.displayName,
      photoURL: data.photoURL,
      role: data.role || 'viewer',
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
    };
  }

  // ユーザー情報更新
  static async updateUser(
    userId: string, 
    updates: { displayName?: string; email?: string; role?: 'admin' | 'viewer' }
  ): Promise<void> {
    try {
      const userRef = doc(db, this.COLLECTION_NAME, userId);
      const updateData: any = {
        updatedAt: Timestamp.now(),
      };
      
      if (updates.displayName !== undefined) {
        updateData.displayName = updates.displayName || null;
      }
      
      if (updates.email !== undefined) {
        updateData.email = updates.email;
      }
      
      if (updates.role !== undefined) {
        updateData.role = updates.role;
      }
      
      await updateDoc(userRef, updateData);
      
      // Firebase Authenticationのユーザー情報も更新
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === userId) {
        if (updates.displayName !== undefined) {
          await updateProfile(currentUser, { displayName: updates.displayName || undefined });
        }
        // メールアドレスの更新は別途実装が必要（sendEmailVerification等）
      }
    } catch (error) {
      console.error('ユーザー情報の更新に失敗しました:', error);
      throw error;
    }
  }

  // ユーザー削除（Firestore と Firebase Authentication の両方から削除。管理者ログイン時はサーバーAPI経由）
  static async deleteUser(userId: string): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('ログインしていません。管理者でログインしてください。');
    }

    const idToken = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'ユーザー削除に失敗しました');
    }
  }

  // 現在ログインしているユーザーをFirestoreに同期（存在しない場合のみ作成）
  static async syncCurrentUser(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return;
      }

      const userRef = doc(db, this.COLLECTION_NAME, currentUser.uid);
      const userSnap = await getDoc(userRef);

      // 既に存在する場合はスキップ
      if (userSnap.exists()) {
        return;
      }

      // Firestoreにユーザー情報を保存
      const userData = {
        email: currentUser.email!,
        displayName: currentUser.displayName || null,
        photoURL: currentUser.photoURL || null,
        role: 'viewer',
        createdAt: Timestamp.fromDate(new Date(currentUser.metadata.creationTime!)),
        updatedAt: Timestamp.fromDate(new Date(currentUser.metadata.lastSignInTime || currentUser.metadata.creationTime!)),
      };

      await setDoc(userRef, userData);
    } catch (error) {
      console.error('ユーザー同期に失敗しました:', error);
      // エラーが発生しても処理を続行
    }
  }
}

